import {useHistory, useParams} from "react-router";
import {useEffect, useState} from "react";
import {deleteDepot, getLogo, getOneDepot, postDepot} from "../../services/httpServices";
import './DepotEditor.css';
import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Stack,
    Switch,
    TextField
} from "@mui/material";
import {useToasts} from "react-toast-notifications";
import {useDepots} from "../../context/Depots/useDepots";
import moment from "moment";
import 'moment/locale/fr'
import getDateRenouvel from "../../utils/getDateRenouvel";
import DocumentCard from "./DocumentCard";
import {DesktopDatePicker} from "@mui/x-date-pickers/DesktopDatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import blobToDataURL from "../../utils/blobToDataURL";

export default function DepotEditor() {
    const {id} = useParams();
    const history = useHistory();
    const {addToast} = useToasts();
    const {refreshDepots, ppays} = useDepots();

    const [dialogDelete, setDialogDelete] = useState(false);
    const [isLoading, setIsLoading] = useState(false)

    const [numero, setNumero] = useState("");
    const [marque, setMarque] = useState("");
    const [titulaire, setTitulaire] = useState("");
    const [dateDepot, setDateDepot] = useState(new Date());
    const [nbRenouvel, setNbRenouvel] = useState(0);
    const [observations, setObservations] = useState("");
    const [territoire, setTerritoire] = useState("fr");
    const [ignoreDelaiPrio, setIgnoreDelaiPrio] = useState(false);
    const [classes, setClasses] = useState("");
    const [pays, setPays] = useState([])
    const [logo, setLogo] = useState(null)
    const [deleteLogo, setDeleteLogo] = useState(false)
    const [urgenceLevel, setUrgenceLevel] = useState("")

    const [documents, setDocuments] = useState([]);
    const [documentsIdsToDelete, setDocumentsIdsToDelete] = useState([]);

    useEffect(() => {
        if (id !== "new") {
            setIsLoading(true)
            getOneDepot(id)
                .then(res => {
                    const depotDTO = res.data
                    const depot = depotDTO?.depot;
                    if (!depot) return addToast("Impossible de récupérer le dépôt", {appearance: "error"})
                    setNumero(depot.numero);
                    setMarque(depot.marque);
                    setTitulaire(depot.titulaire);
                    setDateDepot(depot.dateDepot ? new Date(depot.dateDepot) : null);
                    setNbRenouvel(depot.nbRenouvel);
                    setObservations(depot.observations);
                    setTerritoire(depot.territoire);
                    setIgnoreDelaiPrio(depot.ignoreDelaiPrio);
                    setClasses(depot.classes
                        .split('')
                        .map((n, i) => n === '1' ? String(i + 1) : null)
                        .filter(n => n !== null)
                        .join(';')
                    );
                    setPays(depot.pays ?? [])
                    setUrgenceLevel(depot.urgenceLevel)

                    if (depot.figurative) {
                        getLogo(depot.id)
                            .then(res => blobToDataURL(res.data))
                            .then(setLogo)
                            .catch(() => setLogo(null))
                    }

                    if (depotDTO.documentsDTOs) {
                        setDocuments(depotDTO.documentsDTOs)
                    }
                    setIsLoading(false)
                })
        } else {
            setIsLoading(false)
        }
    }, [id]) //eslint-disable-line

    const handleSubmit = (closeTab) => {
        if (!numero || !numero.match(/^[0-9]+$/)) return addToast("Numero invalide", {appearance: "error"})
        if (!marque) return addToast("Marque manquante", {appearance: "error"})
        if (!titulaire) return addToast("Titulaire manquant", {appearance: "error"})

        // check if classes string contains only numbers and semicolons
        if (classes && !classes.match(/^[0-9;]+$/))
            return addToast("Les classes sont invalides", {appearance: "error"})

        const classesBits = new Array(45).fill('0');
        const classesInputArray = classes.split(';');
        if (classes) {
            for (const strN of classesInputArray) {
                const n = parseInt(strN);
                if (!isNaN(n) && n >= 1 && n <= 45) classesBits[n - 1] = 1;
                else return addToast("Les classes sont invalides", {appearance: "error"});
            }
        }

        postDepot(
            {
                id: id !== "new" ? id : null,
                numero,
                marque,
                titulaire,
                dateDepot,
                nbRenouvel,
                observations,
                territoire,
                ignoreDelaiPrio,
                classes: classesBits.join(''),
                pays,
                documents: null,
            },
            documents,
            documentsIdsToDelete,
            logo,
            deleteLogo
        )
            .then(() => {
                addToast("Dépôt enregistré", {appearance: "success"})
                refreshDepots()
                if (closeTab) window.close()
                else history.push('/')
            })
    }

    const handleDelete = () => setDialogDelete(true);

    const handleClose = () => setDialogDelete(false);

    const onDelete = () => {
        if (id === "new") return;

        deleteDepot(id)
            .then(() => {
                addToast("Dépôt supprimé", {appearance: "success"})
                refreshDepots()
                history.push('/')
            })
    }

    const handleBack = () => history.push('/');

    const change = setter => e => setter(e.target.value);

    const toggleIgnoreDelaiPrio = () => setIgnoreDelaiPrio(v => !v);

    const renouvellerMax = () => setNbRenouvel(getNbRenouvelMax())

    const getNbRenouvelMax = () => {
        const ms = moment().startOf("day").diff(dateDepot)
        const years = moment(ms).year() - 1969
        return Math.ceil(years / 10)
    }

    const dateRenouvel = getDateRenouvel(dateDepot, nbRenouvel)
    let dateRenouvelMax = dateRenouvel
    if (dateRenouvel <= moment().startOf("day")) {
        dateRenouvelMax = moment(dateDepot).startOf("day").add(10 * getNbRenouvelMax(), 'y')
    }

    const changeFiles = e => {
        const files = e.target.files
        if (!files || !files.length) return console.log("no files");

        for (const file of files) {
            if (file.size > 10 * 1000 * 1000) {
                addToast("Le fichier " + file.name + " est trop volumineux", {appearance: "error"})
                continue
            }

            if (documents.find(doc => doc.name === file.name && doc.size === file.size)) {
                addToast("Le fichier " + file.name + " existe déjà", {appearance: "error"})
                continue
            }

            const reader = new FileReader()
            reader.onload = () => {
                const data = reader.result
                const doc = {
                    id: null,
                    name: file.name,
                    size: file.size,
                    path: null,
                    data,
                }
                setDocuments(ds => [...ds, doc])
            }
            reader.readAsDataURL(file)
        }
        e.target.value = null;
    }

    const deleteFile = (d) => {
        if (d.id) setDocumentsIdsToDelete(ds => [...ds, d.id])
        setDocuments(ds => ds.filter(doc => doc.name !== d.name || doc.size !== d.size))
    }

    const changeLogo = e => {
        const file = e.target.files[0]

        if (file.size > 10 * 1000 * 1000) {
            return addToast("Le fichier " + file.name + " est trop volumineux", {appearance: "error"})
        }

        const reader = new FileReader()
        reader.onload = () => {
            const data = reader.result
            setLogo(data)
            setDeleteLogo(false)
        }
        reader.readAsDataURL(file)
    }

    const handleDeleteLogo = () => {
        setLogo(null)
        setDeleteLogo(true)
    }

    if (isLoading) return <>Chargement...</>

    return (
        <div className="depot-editor">
            <h1>{id === "new" ? "Ajouter" : "Éditer"} un dépôt</h1>

            {urgenceLevel === "danger" ? <Alert severity="error">Ce dépôt est expiré</Alert>
                : urgenceLevel === "warning" ? <Alert severity="warning">Ce dépôt expire dans {Math.floor((moment(dateRenouvel) - moment()) / (1000 * 60 * 60 * 24))} jours</Alert>
                    : urgenceLevel === "prio" ? <Alert severity="info">Il reste moins d'un mois pour profiter du délai de priorité</Alert>
                        : urgenceLevel === "prio-info" ? <Alert severity="success">Ce dépôt peut profiter du délai de priorité</Alert> : null}

            <div className="depot-editor-columns">
                <div className="depot-editor-infos">
                    <TextField
                        label="Numéro d'enregistrement"
                        value={numero}
                        onChange={change(setNumero)}
                    />

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <TextField
                            label="Marque"
                            value={marque}
                            onChange={change(setMarque)}
                            style={{width: "50%"}}
                        />

                        <Stack direction="row" spacing={2} alignItems="center">
                            {!!logo && <img src={logo} alt="logo" className="depot-editor-logo"/>}
                            <Stack direction="column">
                                <label className="depot-editor-inputfile">
                                    <span>{logo ? "Changer la" : "Ajouter une"} figure</span>
                                    <input type="file" onChange={changeLogo} accept="image/*" hidden/>
                                </label>
                                {!!logo && <Button variant="contained" onClick={handleDeleteLogo}>Supprimer</Button>}
                            </Stack>
                        </Stack>
                    </Stack>

                    <TextField
                        label="Titulaire"
                        value={titulaire}
                        onChange={change(setTitulaire)}
                    />

                    <Stack direction="row" spacing={2} alignItems="center">
                        <LocalizationProvider dateAdapter={AdapterMoment} locale="frFR">
                            <DesktopDatePicker
                                renderInput={(params) => <TextField {...params} />}
                                label="Date de dépot"
                                inputFormat="DD/MM/YYYY"
                                value={dateDepot}
                                onChange={setDateDepot}
                            />
                        </LocalizationProvider>

                        <TextField
                            label="Date de renouvellement"
                            value={dateRenouvel.format("DD/MM/YYYY")}
                            InputProps={{readOnly: true}}
                        />

                        <TextField
                            label="Renouvellé"
                            type="number"
                            value={nbRenouvel}
                            onChange={e => setNbRenouvel(Number(e.target.value))}
                            min={0}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">fois</InputAdornment>
                            }}
                        />

                        <Button onClick={renouvellerMax} variant="contained" color="primary"
                                disabled={dateRenouvelMax === dateRenouvel}>
                            Marquer comme renouvellé jusqu'au {dateRenouvelMax.format("DD/MM/YYYY")}
                        </Button>
                    </Stack>

                    <TextField select value={territoire} label="Territoire" onChange={change(setTerritoire)}>
                        <MenuItem value="fr">France</MenuItem>
                        <MenuItem value="ue">Union Européenne</MenuItem>
                        <MenuItem value="inter">International</MenuItem>
                    </TextField>

                    {territoire !== "fr" && <Autocomplete
                        multiple
                        options={territoire === "ue" ? ppays.filter(p => p.ue) : ppays.filter(p => !p.ue)}
                        getOptionLabel={v => v.name}
                        renderInput={(params) => <TextField {...params} label="Pays" variant="outlined"/>}
                        onChange={(_, v) => setPays(v)}
                        value={pays}
                    />}

                    {territoire !== "inter" && <div>
                        <FormControlLabel
                            label="Ignorer le délai de priorité"
                            control={<Switch checked={ignoreDelaiPrio} onChange={toggleIgnoreDelaiPrio}/>}
                            labelPlacement="end"
                        />
                    </div>}

                    <TextField
                        label="Classes (séparer les numéros par un point virgule)"
                        value={classes}
                        onChange={change(setClasses)}
                    />

                    <label>
                        Observations
                        <textarea value={observations} onChange={change(setObservations)}/>
                    </label>
                </div>

                <div className="depot-editor-documents">
                    <label className="depot-editor-inputfile">
                        Attacher des documents (max. 10Mo)
                        <input type="file"
                               hidden
                               onChange={changeFiles}
                               multiple/>
                    </label>
                    {!!documents.length ? <ul>
                        {documents.map(d => <DocumentCard doc={d} key={d.name + d.size} deleteFile={deleteFile}/>)}
                    </ul> : <i>Aucun document n'est attaché à ce dépôt</i>}
                </div>
            </div>

            <Button onClick={() => handleSubmit(false)} variant="contained" color="primary">Enregistrer</Button>

            {id !== "new" &&
                <Button onClick={() => handleSubmit(true)} variant="contained" color="primary">
                    Enregistrer et fermer l'onglet
                </Button>}

            <Button onClick={handleBack} variant="contained" color="secondary">Retour à la liste</Button>

            {id !== "new" &&
                <Button onClick={() => window.close()} variant="contained" color="secondary">Fermer l'onglet</Button>}

            {id !== "new" && <Button onClick={handleDelete} variant="outlined">Supprimer le dépôt</Button>}

            <Dialog open={dialogDelete} onClose={handleClose}>
                <DialogTitle>Supprimer ce dépôt ?</DialogTitle>
                <DialogActions>
                    <Button onClick={handleClose} color="secondary" autoFocus>
                        Annuler
                    </Button>
                    <Button onClick={onDelete} color="primary">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
